import { apiClient } from 'src/careflow/ph-core/api_client/api_client';

export const NICHEAIM_TIME_TRACKING_CONFIG_PATH =
  '/host-api/nicheaim-time-tracking/embed/config';
export const NICHEAIM_TIME_TRACKING_TOKEN_PATH =
  '/host-api/nicheaim-time-tracking/embed-token';

export interface NicheAimTimeTrackingConfig {
  enabled: boolean;
  apiBaseUrl: string | null;
}

export interface NicheAimEmbedToken {
  access_token: string;
  token_type: 'Bearer';
  expires_in: number;
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const isSafeApiBaseUrl = (value: unknown): value is string => {
  if (typeof value !== 'string') return false;
  try {
    const url = new URL(value);
    const localDevelopmentHost = [
      'localhost',
      '127.0.0.1',
    ].includes(url.hostname) || url.hostname.endsWith('.localtest.me');
    return !url.username &&
      !url.password &&
      !url.search &&
      !url.hash &&
      url.pathname.replace(/\/+$/, '').endsWith('/api') &&
      (url.protocol === 'https:' || (url.protocol === 'http:' && localDevelopmentHost));
  } catch {
    return false;
  }
};

const parseTimeTrackingConfig = (value: unknown): NicheAimTimeTrackingConfig => {
  if (!isRecord(value) || Object.keys(value).length !== 2 || typeof value.enabled !== 'boolean') {
    throw new Error('NicheAim time-tracking configuration is invalid');
  }
  if (value.enabled === false && value.apiBaseUrl === null) {
    return { enabled: false, apiBaseUrl: null };
  }
  if (value.enabled === true && isSafeApiBaseUrl(value.apiBaseUrl)) {
    return { enabled: true, apiBaseUrl: value.apiBaseUrl };
  }
  throw new Error('NicheAim time-tracking configuration is invalid');
};

export async function getNicheAimTimeTrackingConfig(): Promise<NicheAimTimeTrackingConfig> {
  const response = await apiClient.get<unknown>(
    NICHEAIM_TIME_TRACKING_CONFIG_PATH,
    { timeout: 10_000 },
  );
  return parseTimeTrackingConfig(response);
}

export function getNicheAimEmbedToken() {
  return apiClient.post<Record<string, never>, NicheAimEmbedToken>(
    NICHEAIM_TIME_TRACKING_TOKEN_PATH,
    {},
    { timeout: 15_000 },
  );
}
